import { AGENTIC_FEATURE_GATE_SEEDS } from './feature-gate-agentic-seeds';
import { BRAND_SETUP_FEATURE_GATE_SEEDS } from './feature-gate-brand-setup-seeds';
import { OFFICE_TYPE_FEATURE_GATE_SEEDS } from './feature-gate-office-type-seeds';
import { CATCH_UP_FEATURE_GATE_SEEDS } from './feature-gate-catch-up-seeds';
import { DOCUMENT_FEATURE_GATE_SEEDS } from './feature-gate-document-seeds';
import { DRAFT_COMMITMENT_FEATURE_GATE_SEEDS } from './feature-gate-draft-commitment-seeds';
import { KNOWLEDGE_FEATURE_GATE_SEEDS } from './feature-gate-knowledge-seeds';
import { MINREP_FEATURE_GATE_SEEDS } from './feature-gate-minrep-seeds';
import { OFFICE_MESSENGER_FEATURE_GATE_SEEDS } from './feature-gate-office-messenger-seeds';
import { PEOPLE_FEATURE_GATE_SEEDS } from './feature-gate-people-seeds';
import { SERVER_SEND_FEATURE_GATE_SEEDS } from './feature-gate-server-send-seeds';
import { WEBSITE_FEATURE_GATE_SEEDS } from './feature-gate-website-seeds';

export {
  FEATURE_GATE_RELEASE_STAGES,
  RELEASE_STAGE_DESCRIPTIONS,
  RELEASE_STAGE_LABELS,
} from './feature-gate-release-stages';
export type { FeatureGateReleaseStage } from './feature-gate-release-stages';

// Display order of the grouped sections on the admin Feature Gates page.
export const FEATURE_GATE_GROUPS = [
  'correspondence',
  'pulse_ai',
  'knowledge',
  'campaigns_creative',
  'people_crm',
  'ads_explorer',
  'messaging_channels',
  'platform_security',
] as const;

export type FeatureGateGroup = (typeof FEATURE_GATE_GROUPS)[number];

export interface FeatureGateSeed {
  label: string;
  shortLabel: string;
  description: string;
  group: FeatureGateGroup;
  /** Sidebar nav item ids the gate governs; omit for non-nav gates. */
  navItemIds?: readonly string[];
}

// Single source of truth: keys, definitions, and the fail-closed access
// default are all derived from this map, so a gate cannot exist in one
// registry surface and be missing from another.
const FEATURE_GATE_SEEDS = {
  pulse_ai: {
    label: 'Pulse AI',
    shortLabel: 'AI',
    description: 'Staff agent workspace for drafting, research, and approved Pulse actions.',
    group: 'pulse_ai',
    navItemIds: ['pulse-ai'],
  },
  integrations: {
    label: 'Integrations',
    shortLabel: 'Integrations',
    description: 'Connect Slack and Google for approved use through Pulse AI.',
    group: 'pulse_ai',
    navItemIds: ['integrations'],
  },
  pulse_automations: {
    label: 'Pulse Automations',
    shortLabel: 'Automations',
    description: 'Durable office workflows: discover, turn on, and inspect published automations.',
    group: 'pulse_ai',
    navItemIds: ['discover', 'automations'],
  },
  pulse_agents: {
    label: 'Pulse Agents',
    shortLabel: 'Agents',
    description:
      'Office agents that act for the office under a version an approver published, inside its AI allowance.',
    group: 'pulse_ai',
  },
  draft_learning: {
    label: 'Draft learning',
    shortLabel: 'Draft learning',
    description:
      "Reply drafts learn each mailbox's writing style from its own sent mail. Offices never see what is learned.",
    group: 'pulse_ai',
  },
  ...DRAFT_COMMITMENT_FEATURE_GATE_SEEDS,
  ...AGENTIC_FEATURE_GATE_SEEDS,
  ...WEBSITE_FEATURE_GATE_SEEDS,
  campaign_builder: {
    label: 'Campaign Builder',
    shortLabel: 'Campaigns',
    description: 'EDM and campaign-building workspace for public communications.',
    group: 'campaigns_creative',
    navItemIds: ['campaigns', 'broadcasts', 'brand-kit'],
  },
  broadcast_ab_testing: {
    label: 'Broadcast A/B testing',
    shortLabel: 'A/B testing',
    description:
      'Controlled Broadcast variant testing and winner release. Keep disabled until the decision worker and monitoring are enabled for the office.',
    group: 'campaigns_creative',
  },
  creative_editor: {
    label: 'Creative Editor',
    shortLabel: 'Creative',
    description: 'Design and creative production tools for office communications.',
    group: 'campaigns_creative',
    navItemIds: ['creative'],
  },
  imap_accounts: {
    label: 'IMAP email accounts',
    shortLabel: 'IMAP',
    description:
      'Connect Yahoo, iCloud, and other app-password (IMAP) mailboxes alongside Gmail and Outlook. Not a sidebar item — gates the "Other email accounts" option in the add-account flow.',
    group: 'correspondence',
  },
  messenger: {
    label: 'Facebook Messenger',
    shortLabel: 'Messenger',
    description:
      'Surfaces the Messenger channel (connect Pages, inbox, filters, reporting). Not a sidebar item — the platform GA gate, kept off until Meta App Review of the Public Pulse app clears.',
    group: 'messaging_channels',
  },
  email_external_channel: {
    label: 'External email channel',
    shortLabel: 'External email',
    description:
      'Lets an office handle email correspondence from mailbox identities that Public Pulse does not connect, sync, receive from or send through. Not a sidebar item — it surfaces external mailbox settings, email intake and the copy-and-mark-sent workflow.',
    group: 'correspondence',
  },
  outlook_browser_capture: {
    label: 'Outlook browser capture',
    shortLabel: 'Outlook capture',
    description:
      'Sync Outlook conversations from the browser extension to a selected external inbox and discover possible duplicates across import methods.',
    group: 'correspondence',
  },
  whatsapp: {
    label: 'WhatsApp',
    shortLabel: 'WhatsApp',
    description:
      'Surfaces the WhatsApp channel (connect numbers, inbound, replies, filters, reporting). Not a sidebar item — the platform GA gate, kept off until Meta App Review of the Public Pulse app clears.',
    group: 'messaging_channels',
  },
  whatsapp_coexistence: {
    label: 'WhatsApp coexistence',
    shortLabel: 'Coexistence',
    description:
      'Onboard a number that is already live in the WhatsApp Business app, importing up to 180 days of chat history and mirroring app-sent messages. Not a sidebar item — it gates the second option in the WhatsApp connect flow and requires the whatsapp gate on top, since live onboarding also needs Meta Tech Provider approval.',
    group: 'messaging_channels',
  },
  instagram: {
    label: 'Instagram',
    shortLabel: 'Instagram',
    description:
      'Surfaces the Instagram channel (connect professional accounts, inbound DMs, replies, filters, reporting). Not a sidebar item — the platform GA gate, kept off until Meta App Review of the Public Pulse app clears.',
    group: 'messaging_channels',
  },
  ...SERVER_SEND_FEATURE_GATE_SEEDS,
  ...CATCH_UP_FEATURE_GATE_SEEDS,
  ...BRAND_SETUP_FEATURE_GATE_SEEDS,
  ...OFFICE_TYPE_FEATURE_GATE_SEEDS,
  correspondence_sharing: {
    label: 'Correspondence sharing',
    shortLabel: 'Sharing',
    description:
      'Share an email conversation with specific colleagues (view, comment, or draft). Not a sidebar item — gates the Share button and the "Shared with" panel on a correspondence.',
    group: 'correspondence',
  },
  external_sharing: {
    label: 'External correspondence sharing',
    shortLabel: 'External sharing',
    description:
      'Share a correspondence with people outside the platform via a pin-verified email link or a public link. Not a sidebar item — gates the external-access section of the Share dialog and the org Sharing policy. Governed additionally by the per-org external_share_policy (default disabled).',
    group: 'correspondence',
  },
  sharing_revoke_integrity: {
    label: 'Share revoke integrity',
    shortLabel: 'Revoke integrity',
    description:
      "Server-side checks on ending a correspondence share: only a current office member may revoke, a share cannot be revoked by editing it directly, and revoking a share that never gave access (on a team inbox) leaves the person's assignments and reviews alone. Not a sidebar item - off, revoking works exactly as before.",
    group: 'correspondence',
  },
  ...PEOPLE_FEATURE_GATE_SEEDS,
  ads_explorer: {
    label: 'Ads Explorer',
    shortLabel: 'Ads',
    description:
      'Monitor political ads scraped from the Meta Ad Library — a global feed with filters and per-ad transparency detail. Office-matched relevance is a Phase-2 capability.',
    group: 'ads_explorer',
    navItemIds: ['ads-explorer', 'party-identity'],
  },
  ads_public_sharing: {
    label: 'Ads public sharing',
    shortLabel: 'Ad sharing',
    description:
      'Mint an opaque, time-limited, revocable public link to a single ad — a chrome-free branded landing page. Not a sidebar item — gates the Share button on the ad detail view and the anonymous /ad/:token page.',
    group: 'ads_explorer',
  },
  ads_exposure_score: {
    label: 'Ads exposure scoring',
    shortLabel: 'Ad scoring',
    description:
      'Peer-cohort exposure scoring on Ads Explorer ads: percentile ranks for spend, impressions, audience, and efficiency against party, electorate, issue, and all-Australia cohorts. Not a sidebar item — gates the scoring panel on the ad detail view. Snapshots only exist once ADS_EXPOSURE_SCORING_ENABLED is set on the edge functions.',
    group: 'ads_explorer',
  },
  ...KNOWLEDGE_FEATURE_GATE_SEEDS,
  ...OFFICE_MESSENGER_FEATURE_GATE_SEEDS,
  ministerial_representations: {
    label: 'Ministerial Representations',
    shortLabel: 'Representations',
    description:
      'Author letterhead representations on behalf of constituents with an approval lifecycle. Gates the "Create representation" action on a correspondence and the Representations settings section (letterhead templates + recipient directory).',
    group: 'correspondence',
    navItemIds: ['representations'],
  },
  clip_studio: {
    label: 'Clip Studio',
    shortLabel: 'Clips',
    description:
      'AI video clipping: upload long-form video of the MP, generate captioned brand-styled social clips with guidance-driven selection, review, quick-edit, and export.',
    group: 'campaigns_creative',
    navItemIds: ['clip-studio'],
  },
  clip_subject_reframe: {
    label: 'Clip Studio subject reframing',
    shortLabel: 'Subject framing',
    description:
      'Uses local dominant-face detection to persist an initial left or right anchor for portrait Clip Studio exports when the subject is consistently off-centre; otherwise the absent anchor keeps the centred crop. Not a sidebar item - keep disabled except during controlled post-merge validation, and restore it afterwards unless rollout is separately approved.',
    group: 'campaigns_creative',
  },
  composer_workspace: {
    label: 'Composer workspace',
    shortLabel: 'Composer',
    description:
      'The connected video composer: a full-page transcript + canvas + timeline workspace over one composition. Not a sidebar item — it gates the deep-link workspace route reached from a Clip Studio composition, so off means the route fails closed to Clip Studio.',
    group: 'campaigns_creative',
  },
  brand_studio: {
    label: 'Brand Studio',
    shortLabel: 'Brand',
    description:
      'Universal organisation brand kit: palette, gradients, typography, logos, voice, and social rules powering creative and broadcast output.',
    group: 'campaigns_creative',
    navItemIds: ['brand-kit'],
  },
  email_backfill_admission: {
    label: 'Email backfill admission control',
    shortLabel: 'Backfill admission',
    description:
      'Routes initial-sync backfills through the admission queue with global and per-office concurrency caps. Not a sidebar item — off means every entry point enqueues nothing and syncs exactly as before.',
    group: 'correspondence',
  },
  enterprise_crm_import: {
    label: 'Enterprise CRM import',
    shortLabel: 'CRM import',
    description:
      'Durable, resumable CRM contact import (CSV/HubSpot/Mailchimp) with server-side parsing, typed validation, preflight counts, and set-based apply. Not a sidebar item — off means the import wizard and the import-jobs API are unavailable.',
    group: 'people_crm',
  },
  passkeys: {
    label: 'Passkeys',
    shortLabel: 'Passkeys',
    description:
      'Passwordless sign-in with a device passkey (Face ID, Touch ID, security key) plus enrollment and management in Security settings. Not a sidebar item - off means no new enrollments; passkeys already enrolled stay manageable and can still sign in.',
    group: 'platform_security',
  },
  reply_from_personal_on_shared: {
    label: 'Reply from personal account',
    shortLabel: 'Personal reply',
    description:
      'Lets a delegate reply to a shared-inbox correspondence from their own connected personal account, with the on-behalf audit record. Not a sidebar item — off means the composer never offers a personal sender.',
    group: 'correspondence',
  },
  ...DOCUMENT_FEATURE_GATE_SEEDS,
  ...MINREP_FEATURE_GATE_SEEDS,
  offline_cache: {
    label: 'Offline cache',
    shortLabel: 'Offline',
    description:
      'Caches correspondence, threads, tasks, and notifications in an encrypted on-device store so an office can read them without a connection. iOS app only - web behaviour is unchanged. Not a sidebar item - off means nothing further is written to the device, every surface reads live only, and rows already cached for the office are purged at the next online check.',
    group: 'platform_security',
  },
} satisfies Record<string, FeatureGateSeed>;

export type FeatureGateKey = keyof typeof FEATURE_GATE_SEEDS;

export interface FeatureGateDefinition {
  key: FeatureGateKey;
  label: string;
  shortLabel: string;
  description: string;
  group: FeatureGateGroup;
  navItemIds: readonly string[];
}

export type FeatureGateAccess = Record<FeatureGateKey, boolean>;

export const FEATURE_GATE_KEYS = Object.keys(FEATURE_GATE_SEEDS) as readonly FeatureGateKey[];

export const FEATURE_GATE_DEFINITIONS: readonly FeatureGateDefinition[] = FEATURE_GATE_KEYS.map(
  (key) => ({ key, navItemIds: [], ...FEATURE_GATE_SEEDS[key] }),
);

export const EMPTY_FEATURE_ACCESS = Object.fromEntries(
  FEATURE_GATE_KEYS.map((key) => [key, false]),
) as FeatureGateAccess;
